/**
 * Temperature Role - Validate Pillar
 *
 * Validação semântica de valores de temperatura.
 * Zero dependências externas - pode ser usado standalone.
 *
 * IMPORTANTE: Temperatura não pode ser abaixo do zero absoluto.
 * O zero absoluto varia por escala:
 * - Celsius: -273.15°C
 * - Fahrenheit: -459.67°F
 * - Kelvin: 0K
 * - Rankine: 0°R
 *
 * @example
 * import { validateTemperature, isValidTemperature } from '@attrx/role-morphic/temperature/validate';
 *
 * validateTemperature(100, 'celsius');     // { valid: true, errors: [] }
 * validateTemperature(-300, 'celsius');    // { valid: false, errors: ['...below absolute zero...'] }
 * validateTemperature(-10, 'kelvin');      // { valid: false, errors: ['...below absolute zero...'] }
 * isValidTemperature(100, 'celsius');      // true
 */

import { type TemperatureUnit, TEMPERATURE_CONSTANTS } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface TemperatureValidationConfig {
  /** Permite temperaturas abaixo do zero absoluto (default: false) */
  allowBelowAbsoluteZero?: boolean;
  /** Valor máximo permitido */
  max?: number;
  /** Mensagem de erro para max */
  maxError?: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Zero absoluto em cada escala
 */
export const ABSOLUTE_ZERO: Record<TemperatureUnit, number> = {
  celsius: TEMPERATURE_CONSTANTS.ABSOLUTE_ZERO_CELSIUS,
  fahrenheit: TEMPERATURE_CONSTANTS.ABSOLUTE_ZERO_FAHRENHEIT,
  kelvin: 0,
  rankine: 0,
};

/**
 * Símbolos para mensagens de erro
 */
const UNIT_SYMBOLS: Record<TemperatureUnit, string> = {
  celsius: "°C",
  fahrenheit: "°F",
  kelvin: "K",
  rankine: "°R",
};

// =============================================================================
// VALIDATE
// =============================================================================

/**
 * Valida um valor de temperatura
 *
 * @param value - Valor a ser validado
 * @param unit - Unidade do valor (default: celsius)
 * @param config - Configuração customizada (opcional)
 * @returns Resultado da validação com erros se houver
 *
 * @example
 * validateTemperature(100, 'celsius');              // { valid: true, errors: [] }
 * validateTemperature(-300, 'celsius');             // { valid: false, errors: ['...below absolute zero...'] }
 * validateTemperature(-10, 'kelvin');               // { valid: false, errors: ['...below absolute zero...'] }
 * validateTemperature(0, 'kelvin');                 // { valid: true, errors: [] } (0K = zero absoluto)
 * validateTemperature(-459.67, 'fahrenheit');       // { valid: true, errors: [] } (zero absoluto em °F)
 */
export function validateTemperature(
  value: unknown,
  unit: TemperatureUnit = "celsius",
  config: TemperatureValidationConfig = {}
): ValidationResult {
  const errors: string[] = [];

  // Type check
  if (typeof value !== "number") {
    return { valid: false, errors: ["Value must be a number"] };
  }

  // Finite check
  if (!Number.isFinite(value)) {
    return { valid: false, errors: ["Value must be finite"] };
  }

  // Absolute zero check (unless explicitly allowed)
  if (!config.allowBelowAbsoluteZero) {
    const absoluteZero = ABSOLUTE_ZERO[unit];
    if (value < absoluteZero) {
      const symbol = UNIT_SYMBOLS[unit];
      errors.push(
        `Temperature cannot be below absolute zero (${absoluteZero}${symbol})`
      );
    }
  }

  // Max check
  if (config.max !== undefined && value > config.max) {
    const msg = config.maxError ?? `Value must be at most ${config.max}`;
    errors.push(msg);
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Verifica se um valor de temperatura é válido
 *
 * @param value - Valor a ser validado
 * @param unit - Unidade do valor (default: celsius)
 * @param config - Configuração customizada (opcional)
 * @returns true se válido, false caso contrário
 *
 * @example
 * isValidTemperature(100, 'celsius');   // true
 * isValidTemperature(-300, 'celsius');  // false
 * isValidTemperature(-10, 'kelvin');    // false
 */
export function isValidTemperature(
  value: unknown,
  unit: TemperatureUnit = "celsius",
  config: TemperatureValidationConfig = {}
): boolean {
  return validateTemperature(value, unit, config).valid;
}

/**
 * Retorna o zero absoluto na escala especificada
 *
 * @example
 * getAbsoluteZero('celsius');     // -273.15
 * getAbsoluteZero('fahrenheit');  // -459.67
 * getAbsoluteZero('kelvin');      // 0
 */
export function getAbsoluteZero(unit: TemperatureUnit): number {
  return ABSOLUTE_ZERO[unit];
}
